import aiAPI from '@edulastic/api/src/aiAPI'
import { segmentApi } from '@edulastic/api'
import { isEmpty } from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { notification } from '@edulastic/common'
import {
  getCurrentQuestionSelector,
  UPDATE_QUESTION_REQUEST,
} from '../../sharedDucks/questions'
import {
  getDictionariesAlignmentsSelector,
  getCurriculumsListSelector,
} from '../../src/selectors/dictionaries'
import { getInterestedGradesSelector } from '../../src/selectors/user'
import { SET_ALIGNMENT_FROM_QUESTION } from '../../src/constants/actions'
import {
  applyAIMetadataToQuestion,
  buildDictAlignmentFromStandards,
  buildQuestionMetadataPayload,
  getFormattedAISuggestedData,
  getEffectiveMetadata,
} from '../utils/AIQuestionAssistance'
import { getItemIdSelector } from '../../sharedDucks/itemDetail'

export const namespaceKey = 'aiQuestionAssistance'

// actions constants
export const GENERATE_AI_QUESTION_METADATA = `[${namespaceKey}] generate question metadata`
export const SET_AI_QUESTION_METADATA_STATE = `[${namespaceKey}] set state`
export const RESET_AI_QUESTION_METADATA_STATE = `[${namespaceKey}] reset metadata state`
export const APPLY_AI_SUGGESTED_METADATA = `[${namespaceKey}] apply suggested metadata`
export const UPDATE_USER_UPDATED_DATA = `[${namespaceKey}] update user updated data`
export const RESET_USER_UPDATED_DATA = `[${namespaceKey}] reset user updated data`

// actions
export const generateAIQuestionMetadataAction = (payload) => ({
  type: GENERATE_AI_QUESTION_METADATA,
  payload,
})

export const setAIQuestionMetadataStateAction = (payload) => ({
  type: SET_AI_QUESTION_METADATA_STATE,
  payload,
})

export const resetAIQuestionMetadataStateAction = () => ({
  type: RESET_AI_QUESTION_METADATA_STATE,
})

export const applyAISuggestedMetadataAction = () => ({
  type: APPLY_AI_SUGGESTED_METADATA,
})

export const updateUserUpdatedDataAction = (payload) => ({
  type: UPDATE_USER_UPDATED_DATA,
  payload,
})

export const resetUserUpdatedDataAction = () => ({
  type: RESET_USER_UPDATED_DATA,
})

// selectors
export const stateSelector = (state) => state?.[namespaceKey]

export const getAIQuestionAssistanceState = createSelector(
  stateSelector,
  (state) => state || {}
)

export const getSuggestedMetadataSelector = createSelector(
  getAIQuestionAssistanceState,
  (state) => state?.result?.suggestedMetaData
)

export const getUserUpdatedDataSelector = createSelector(
  getAIQuestionAssistanceState,
  (state) => state?.result?.userUpdatedData || {}
)

// initial state
const initialState = {
  apiStatus: null,
  result: { suggestedMetaData: null, userUpdatedData: {} },
  errorMessage: null,
}

// reducer
export const reducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case SET_AI_QUESTION_METADATA_STATE:
      return {
        ...state,
        ...payload,
      }
    case RESET_AI_QUESTION_METADATA_STATE:
      return {
        ...state,
        result: {
          ...state.result,
          suggestedMetaData: null,
        },
      }
    case UPDATE_USER_UPDATED_DATA:
      return {
        ...state,
        result: {
          ...state.result,
          userUpdatedData: {
            ...(state.result?.userUpdatedData || {}),
            ...payload,
          },
        },
      }
    case RESET_USER_UPDATED_DATA:
      return {
        ...state,
        result: {
          ...state.result,
          userUpdatedData: {},
        },
      }
    default:
      return state
  }
}

// sagas
function* generateAIQuestionMetadataSaga({ payload }) {
  const generalErrorMessage = 'Failed to generate metadata suggestions'
  const {
    question,
    testId = null,
    selectedStandardSetId = null,
    subject = null,
  } = payload || {}

  try {
    yield put(
      setAIQuestionMetadataStateAction({
        apiStatus: 'INITIATED',
        result: null,
        errorMessage: null,
      })
    )

    const metadataPayload = buildQuestionMetadataPayload(question)

    const interestedGrades = yield select(getInterestedGradesSelector)
    const itemId = yield select(getItemIdSelector)

    const startTime = Date.now()

    const result = yield call(aiAPI.generateQuestionMetadata, {
      ...metadataPayload,
      interestedCurriculumIds: selectedStandardSetId
        ? [selectedStandardSetId]
        : [],
      ...(subject ? { subject } : {}),
      ...(interestedGrades?.length > 0 ? { grades: interestedGrades } : {}),
    })

    yield put(
      setAIQuestionMetadataStateAction({
        apiStatus: 'SUCCESS',
        result: {
          suggestedMetaData: result,
          userUpdatedData: {},
        },
        errorMessage: null,
      })
    )

    const responseTimeSec = +((Date.now() - startTime) / 1000).toFixed(1)

    segmentApi.genericEventTrack(
      'AIQuestionMetadata:Generated',
      {
        flow: testId ? 'testAuthoring' : 'itemAuthoring',
        itemId: itemId || null,
        qid: question?.id || null,
        testId: testId || null,
        sessionId: window?.sessionStorage?.kid,
        questionType: question?.type,
        questionTitle: question?.title,
        standardId: result?.standards?.[0]?._id || null,
        dok: result?.dok || null,
        difficulty: result?.difficulty || null,
        timeTakenInSeconds: responseTimeSec,
      },
      { attachUserData: true }
    )
  } catch (err) {
    yield put(
      setAIQuestionMetadataStateAction({
        apiStatus: 'FAILED',
        result: null,
        errorMessage: generalErrorMessage,
      })
    )
  }
}

export function* applyAISuggestedMetadataSaga() {
  const suggestedMetaData = yield select(getSuggestedMetadataSelector)
  const userUpdatedData = yield select(getUserUpdatedDataSelector)

  if (isEmpty(suggestedMetaData)) return

  const metadata = getEffectiveMetadata({
    suggestedMetaData,
    userUpdatedData,
  })

  let question = yield select(getCurrentQuestionSelector)
  let alignments = yield select(getDictionariesAlignmentsSelector)
  const allCurriculums = yield select(getCurriculumsListSelector)

  question = applyAIMetadataToQuestion({
    question,
    metadata,
    allCurriculums,
    dictAlignments: alignments,
  })

  const aiSuggestedMetadata = getFormattedAISuggestedData(suggestedMetaData)
  if (!isEmpty(aiSuggestedMetadata)) {
    question.aiSuggestedMetadata = aiSuggestedMetadata
  }

  yield put({ type: UPDATE_QUESTION_REQUEST, payload: question })

  notification({
    type: 'success',
    msg: 'Suggestions applied successfully',
  })

  alignments = buildDictAlignmentFromStandards(
    metadata?.standards || [],
    allCurriculums,
    alignments
  )

  yield put(resetAIQuestionMetadataStateAction())
  yield put(resetUserUpdatedDataAction())
  yield put({ type: SET_ALIGNMENT_FROM_QUESTION, payload: alignments })
}

export function* watcherSaga() {
  yield all([
    takeLatest(GENERATE_AI_QUESTION_METADATA, generateAIQuestionMetadataSaga),
    takeLatest(APPLY_AI_SUGGESTED_METADATA, applyAISuggestedMetadataSaga),
  ])
}
